import { useCallback, useRef } from "react";

interface GestureCallbacks {
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onPan?: () => void;
  onPanEnd?: () => void;
}

const useGesture = ({ onZoomIn, onZoomOut, onPan, onPanEnd }: GestureCallbacks) => {
  const startDistanceRef = useRef<number | null>(null);
  const actionReportedRef = useRef<{ zoom: boolean; pan: boolean }>({
    zoom: false,
    pan: false,
  });

  const resetActionFlags = () => {
    actionReportedRef.current.zoom = false;
    actionReportedRef.current.pan = false;
  };

  const getDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.hypot(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length === 2) {
        startDistanceRef.current = getDistance(event.touches[0], event.touches[1]);
        resetActionFlags();
      } else if (event.touches.length === 1) {
        resetActionFlags();
        onPan?.();
        actionReportedRef.current.pan = true;
      }
    },
    [onPan],
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLElement>) => {
      if (event.touches.length === 2 && startDistanceRef.current !== null) {
        const currentDistance = getDistance(event.touches[0], event.touches[1]);
        if (!actionReportedRef.current.zoom) {
          if (currentDistance > startDistanceRef.current) {
            onZoomIn?.();
          } else if (currentDistance < startDistanceRef.current) {
            onZoomOut?.();
          }
          actionReportedRef.current.zoom = true;
        }
      } else if (event.touches.length === 1 && !actionReportedRef.current.pan) {
        onPan?.();
        actionReportedRef.current.pan = true;
      }
    },
    [onZoomIn, onZoomOut, onPan],
  );

  const handleTouchEnd = useCallback(() => {
    startDistanceRef.current = null;
    resetActionFlags();
    onPanEnd?.();
  }, [onPanEnd]);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};

export default useGesture;
